// Theme store — tiny pub/sub around localStorage. Defaults to dark.
// Applied to <html data-theme="dark|light"> so CSS variables in index.css
// can branch. The pre-hydration bootstrap in main.tsx sets the attribute
// before React paints to avoid a theme flash on first load.

export type Theme = 'dark' | 'light';

const STORAGE_KEY = 'rlpscout.theme';
const DEFAULT_THEME: Theme = 'dark';

function readStored(): Theme {
  if (typeof window === 'undefined') return DEFAULT_THEME;
  const v = window.localStorage.getItem(STORAGE_KEY);
  return v === 'light' || v === 'dark' ? v : DEFAULT_THEME;
}

function applyToDocument(theme: Theme): void {
  const root = document.documentElement;
  root.setAttribute('data-theme', theme);
  root.style.colorScheme = theme;
}

export function getTheme(): Theme {
  return readStored();
}

export function setTheme(theme: Theme): void {
  window.localStorage.setItem(STORAGE_KEY, theme);
  applyToDocument(theme);
  window.dispatchEvent(new CustomEvent('rlpscout:theme', { detail: theme }));
}

export function toggleTheme(): Theme {
  const next: Theme = getTheme() === 'dark' ? 'light' : 'dark';
  setTheme(next);
  return next;
}

// Subscribe to theme changes (used by useSyncExternalStore in React).
export function subscribeTheme(listener: () => void): () => void {
  const handler = () => listener();
  window.addEventListener('rlpscout:theme', handler);
  window.addEventListener('storage', handler);
  return () => {
    window.removeEventListener('rlpscout:theme', handler);
    window.removeEventListener('storage', handler);
  };
}

// Called once from main.tsx before React renders.
export function initTheme(): void {
  applyToDocument(readStored());
}
